import React, { useEffect, useState } from 'react';
import { FiClock, FiRefreshCw } from 'react-icons/fi';
import PageTitle from '../PageTitle';

const API_BASE = process.env.REACT_APP_API_URL || 'https://ccro-api.ccroinida.workers.dev';

interface PageData {
  slug: string;
  title: string;
  content: string;
  updated_at: string;
  updated_by: string | null;
}

// Server timestamps are stored in UTC — always display in IST (UTC+5:30)
export function formatIstDateTime(utcString: string | null | undefined): string {
  if (!utcString) return '';
  const d = new Date(utcString);
  if (isNaN(d.getTime())) return '';
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  let hours = ist.getUTCHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${pad(ist.getUTCDate())}/${pad(ist.getUTCMonth() + 1)}/${ist.getUTCFullYear()}, ${pad(hours)}:${pad(ist.getUTCMinutes())}:${pad(ist.getUTCSeconds())} ${ampm} IST`;
}

interface PolicyPageProps {
  slug: string;
  subtitle: string;
}

// Content-managed public page. Content is plain text from the admin editor and
// is rendered exactly as pasted (whitespace preserved, no HTML interpretation).
const PolicyPage: React.FC<PolicyPageProps> = ({ slug, subtitle }) => {
  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadPage = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API_BASE}/api/pages/${slug}`);
      const data = await res.json();
      if (res.ok && data.page) {
        setPage(data.page);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return (
    <div className="bg-gray-900 min-h-screen">
      <PageTitle title={page?.title || subtitle} subtitle={subtitle} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 -mt-10">
        <div className="relative rounded-2xl border border-white/10 bg-gray-800/60 backdrop-blur-sm shadow-2xl overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

          <div className="px-6 sm:px-10 py-8 md:px-12 md:py-10">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
                <FiRefreshCw className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading content...</span>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-gray-300 text-lg font-medium mb-2">Content is temporarily unavailable</p>
                <p className="text-gray-500 text-sm mb-6">Please check your connection and try again.</p>
                <button
                  onClick={loadPage}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                >
                  <FiRefreshCw className="w-4 h-4" />
                  Retry
                </button>
              </div>
            ) : (
              <>
                {page?.updated_at && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 border-b border-white/10 pb-4 mb-6">
                    <FiClock className="w-3.5 h-3.5 text-blue-400" />
                    <span>
                      Last Updated:{' '}
                      <span className="text-gray-200 font-medium">{formatIstDateTime(page.updated_at)}</span>
                      {page.updated_by ? <span className="text-gray-500"> by {page.updated_by}</span> : null}
                    </span>
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words text-gray-200 text-[15px] leading-7">
                  {page?.content}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyPage;
